// Push guide (§14.4 "Push guide: Auto (on until Bean Level 5) / On / Off"): a teaching overlay on
// the ground for the local bean. When another bean is inside shove range and the §2.5 hit cone, it
// marks that target and draws where a shove would send it: a dashed arrow along the §2.5 knockback
// direction, as long as the §2.8 slide at the target's Dizzy. If the slide crosses the edge (or the
// target is teetering / hanging, where any hit is a ring-out), the arrow turns red and points off the
// rim — "this shove is a KO". Everything is computed from replicated state; nothing is simulated.
import { TUNE } from '@shared/constants';
import { BS } from '@shared/ids';
import type { BeanView, FrameView } from '../../game/FrameView';
import { Cell } from './atlas';
import type { DecalSystem } from './Decals';
import { C } from './effects';
import type { GroundFn } from './Telegraphs';

export const PUSH_GUIDE = {
  /** Lunge travel (13 m/s × 9 ticks) plus the hit reach between two beans. */
  range: (TUNE.shove.lungeSpeed * TUNE.shove.lungeTicks) / 60 + TUNE.shove.reach + 2 * TUNE.bean.radius,
  halfArcRad: (TUNE.shove.halfArcDeg * Math.PI) / 180,
  /** Slide ≈ kb² / (2 · tumble decel): 3.2 / 8.2 / 15.5 m at 0 / 50 / 100 Dizzy (§2.8: 3.0 / 7.9 / 15). */
  slidePerKb2: 1 / (2 * TUNE.tumble.decel),
  step: 0.3,
  dash: 0.32,
} as const;

/** States a shove can be started from (§2.5). */
function canShove(b: BeanView): boolean {
  return b.state === BS.NORMAL || b.state === BS.CARRYING;
}

function hittable(b: BeanView): boolean {
  return b.active && !b.out && !b.ghost && !b.invuln
    && b.state !== BS.FALLING && b.state !== BS.RESPAWN && b.state !== BS.SPUN && b.state !== BS.GHOST && b.state !== BS.GRABBED;
}

export interface PushPrediction { target: number; dirX: number; dirY: number; slide: number; ringOut: boolean; edgeAt: number }

export class PushGuide {
  /** The last frame's prediction (verification), or null. */
  last: PushPrediction | null = null;
  private readonly pred: PushPrediction = { target: -1, dirX: 0, dirY: 0, slide: 0, ringOut: false, edgeAt: -1 };
  private time = 0;

  constructor(private readonly decals: DecalSystem, private readonly ground: GroundFn) {}

  /** Picks the target and predicts the shove (exported for tests); null if nothing is in range. */
  predict(view: FrameView): PushPrediction | null {
    const me = view.localSlot < view.beans.length ? view.beans[view.localSlot] : null;
    if (!me || !me.active || me.out || me.ghost || !canShove(me)) return null;
    const fx = Math.cos(me.facing);
    const fy = Math.sin(me.facing);
    let best = -1;
    let bestScore = Infinity;
    for (const b of view.beans) {
      if (b.slot === me.slot || !hittable(b)) continue;
      if (me.team !== 0 && b.team === me.team) continue;
      const dx = b.x - me.x;
      const dy = b.y - me.y;
      const dist = Math.hypot(dx, dy);
      if (dist > PUSH_GUIDE.range || dist < 1e-3) continue;
      const ang = Math.acos(Math.max(-1, Math.min(1, (dx * fx + dy * fy) / dist)));
      if (ang > PUSH_GUIDE.halfArcRad) continue;
      if (Math.abs(b.z - me.z) > TUNE.shove.maxDz + 0.3) continue;
      const score = dist * (1 + ang / PUSH_GUIDE.halfArcRad);
      if (score < bestScore) {
        bestScore = score;
        best = b.slot;
      }
    }
    if (best < 0) return null;
    const t = view.beans[best];
    const dx = t.x - me.x;
    const dy = t.y - me.y;
    const dl = Math.hypot(dx, dy);
    let kx = TUNE.shove.dirBlend * (dx / dl) + (1 - TUNE.shove.dirBlend) * fx;
    let ky = TUNE.shove.dirBlend * (dy / dl) + (1 - TUNE.shove.dirBlend) * fy;
    const kl = Math.hypot(kx, ky) || 1;
    kx /= kl;
    ky /= kl;
    const airborne = t.z > TUNE.bean.airborneZ;
    const kb = TUNE.shove.kb * (1 + TUNE.dizzy.kbPerPoint * t.dizzy) * (airborne ? TUNE.knock.airborneMult : 1)
      * (me.big ? TUNE.shake.kbDealt : 1) * (t.big ? TUNE.shake.kbTaken : 1);
    const p = this.pred;
    p.target = best;
    p.dirX = kx;
    p.dirY = ky;
    p.slide = Math.max(1, kb * kb * PUSH_GUIDE.slidePerKb2);
    p.edgeAt = -1;
    const onEdge = t.state === BS.TEETER || t.state === BS.LEDGE || t.state === BS.CLIMB;
    if (onEdge) {
      p.ringOut = true;
      p.edgeAt = 0;
      p.slide = Math.max(2, p.slide * 0.3);
      return p;
    }
    p.ringOut = false;
    for (let d = PUSH_GUIDE.step; d <= p.slide; d += PUSH_GUIDE.step) {
      if (!Number.isFinite(this.ground(t.x + kx * d, t.y + ky * d))) {
        p.ringOut = true;
        p.edgeAt = d;
        break;
      }
    }
    return p;
  }

  update(view: FrameView, dt: number, enabled: boolean): void {
    this.time += dt;
    this.last = enabled ? this.predict(view) : null;
    const p = this.last;
    if (!p) return;
    const t = view.beans[p.target];
    const s = this.decals.spec;
    const color = p.ringOut ? C.danger : C.white;
    const h = this.ground(t.x, t.y);
    const base = Number.isFinite(h) ? h : 0;
    // Target reticle.
    s.reset();
    s.x = t.x;
    s.y = t.y;
    s.h = base;
    s.width = s.length = 1.35 + 0.08 * Math.sin(this.time * 8);
    s.cell = Cell.TARGET;
    s.color(color);
    s.alpha = p.ringOut ? 0.95 : 0.7;
    this.decals.draw(s);
    // Dashed arrow along the knockback, marching outward; a ring-out arrow ends past the rim.
    const len = p.ringOut ? (p.edgeAt > 0 ? p.edgeAt + 0.9 : p.slide) : Math.min(p.slide, 6);
    const start = 0.75;
    const march = (this.time * 1.6) % PUSH_GUIDE.dash;
    const angle = Math.atan2(p.dirY, p.dirX);
    for (let d = start + march; d < len - 0.55; d += PUSH_GUIDE.dash * 1.6) {
      const gh = this.ground(t.x + p.dirX * d, t.y + p.dirY * d);
      s.reset();
      s.x = t.x + p.dirX * d;
      s.y = t.y + p.dirY * d;
      s.h = Number.isFinite(gh) ? gh : base;
      s.angle = angle;
      s.width = 0.42;
      s.length = PUSH_GUIDE.dash;
      s.cell = Cell.DASH;
      s.color(color);
      s.alpha = (p.ringOut ? 0.9 : 0.6) * Math.min(1, (len - d) / 1.2);
      this.decals.draw(s);
    }
    const hx = t.x + p.dirX * Math.max(start + 0.3, len - 0.35);
    const hy = t.y + p.dirY * Math.max(start + 0.3, len - 0.35);
    const hh = this.ground(hx, hy);
    s.reset();
    s.x = hx;
    s.y = hy;
    s.h = Number.isFinite(hh) ? hh : base;
    s.angle = angle;
    s.width = s.length = p.ringOut ? 0.9 : 0.7;
    s.cell = Cell.ARROW;
    s.color(color);
    s.alpha = p.ringOut ? 0.95 : 0.65;
    this.decals.draw(s);
  }
}
